import {
  CognitoUser,
  AuthenticationDetails,
  CognitoUserPool,
  CognitoUserSession,
} from "amazon-cognito-identity-js";

import { getUserPool } from "./cognito-config";

/**
 * Interface for Cognito JWT tokens returned after successful authentication
 */
export interface CognitoTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
}

/**
 * Interface for Cognito authentication errors
 */
export interface CognitoAuthError {
  code: string;
  name: string;
  message: string;
}

/**
 * Special error code for NEW_PASSWORD_REQUIRED challenge
 */
export const NEW_PASSWORD_REQUIRED = "NewPasswordRequired";

/**
 * Authenticate user with AWS Cognito using email and password
 *
 * @param email - User's email address
 * @param password - User's password
 * @returns Promise resolving to CognitoTokens (idToken, accessToken, refreshToken)
 * @throws CognitoAuthError if authentication fails, or NEW_PASSWORD_REQUIRED error if user needs to set password
 *
 */
export function authenticateCognito(
  email: string,
  password: string,
): Promise<CognitoTokens> {
  return new Promise((resolve, reject) => {
    const userPool = getUserPool();

    if (!userPool) {
      reject({
        code: "ConfigurationError",
        name: "CognitoConfigurationError",
        message: "Cognito is not configured",
      } as CognitoAuthError);
      return;
    }

    const authenticationDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    });

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (session: CognitoUserSession) => {
        resolve({
          idToken: session.getIdToken().getJwtToken(),
          accessToken: session.getAccessToken().getJwtToken(),
          refreshToken: session.getRefreshToken().getToken(),
        });
      },

      onFailure: (err: CognitoAuthError) => {
        // Map common Cognito errors to consistent error format
        reject({
          code: err.code || "AuthenticationFailed",
          name: err.name || "CognitoAuthenticationError",
          message: err.message || "Authentication failed",
        } as CognitoAuthError);
      },

      newPasswordRequired: (userAttributes, requiredAttributes) => {
        // User must set a new password (first login with temporary password)
        reject({
          code: NEW_PASSWORD_REQUIRED,
          name: "NewPasswordRequired",
          message: "User must set a new password",
        } as CognitoAuthError);
      },
    });
  });
}

/**
 * Complete new password challenge for users created via AdminCreateUser
 * This handles the NEW_PASSWORD_REQUIRED challenge when users log in with temporary passwords
 *
 * @param email - User's email address
 * @param temporaryPassword - Temporary password from Cognito email
 * @param newPassword - New permanent password to set
 * @returns Promise resolving to CognitoTokens (idToken, accessToken, refreshToken)
 * @throws CognitoAuthError if password change fails
 */
export function completeNewPassword(
  email: string,
  temporaryPassword: string,
  newPassword: string,
): Promise<CognitoTokens> {
  return new Promise((resolve, reject) => {
    const userPool = getUserPool();

    if (!userPool) {
      reject({
        code: "ConfigurationError",
        name: "CognitoConfigurationError",
        message: "Cognito is not configured",
      } as CognitoAuthError);
      return;
    }

    const authenticationDetails = new AuthenticationDetails({
      Username: email,
      Password: temporaryPassword,
    });

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (session: CognitoUserSession) => {
        // User logged in successfully without needing password change
        resolve({
          idToken: session.getIdToken().getJwtToken(),
          accessToken: session.getAccessToken().getJwtToken(),
          refreshToken: session.getRefreshToken().getToken(),
        });
      },

      onFailure: (err: CognitoAuthError) => {
        reject({
          code: err.code || "AuthenticationFailed",
          name: err.name || "CognitoAuthenticationError",
          message: err.message || "Authentication failed",
        } as CognitoAuthError);
      },

      newPasswordRequired: (userAttributes, requiredAttributes) => {
        // Delete attributes that cannot be updated
        delete userAttributes.email;

        // Mark email as verified since user successfully logged in with temp password from email
        userAttributes.email_verified = "true";

        // Complete the new password challenge
        cognitoUser.completeNewPasswordChallenge(newPassword, userAttributes, {
          onSuccess: (session: CognitoUserSession) => {
            resolve({
              idToken: session.getIdToken().getJwtToken(),
              accessToken: session.getAccessToken().getJwtToken(),
              refreshToken: session.getRefreshToken().getToken(),
            });
          },

          onFailure: (err: CognitoAuthError) => {
            reject({
              code: err.code || "NewPasswordFailed",
              name: err.name || "NewPasswordError",
              message: err.message || "Failed to set new password",
            } as CognitoAuthError);
          },
        });
      },
    });
  });
}
